import { Chart as ChartJS } from "chart.js";
import { BASE_SIZE_MIN_VALUE } from "../../constants";
import { wrapChartCharacters } from "../../commonLogics";

export const getOuncePerUSeChartOptions = (usePerOunceGraph, activeTag) => {
  const datasets = usePerOunceGraph?.datasets || [];
  const maxValue =
    Math.max(...datasets.flatMap((dataset) => dataset.data), 0) + 2;
  return {
    aspectRatio: 3,
    barThickness: datasets.length === 1 ? 300 : undefined,
    plugins: {
      legend: {
        display: false,
        position: "top",
        align: "start",
        labels: {
          padding: 20,
          font: { size: 12, weight: "bold" },
          color: "#6C757D",
        },
      },
      datalabels: {
        // color: "#000",
        // anchor: "end",
        // align: "top",
        // font: { weight: "bold", size: 12 },
        // formatter: (value) => (value !== 0 ? value : ""),
        labels: {
          value: {
            align: "center",
            anchor: "center",
            color: "#fff",
            font: { weight: "bold", size: 14 },
            formatter: (value) =>
              value !== 0 ? parseFloat(value).toFixed(1) : "",
          },
          name: {
            align: "end",
            anchor: "end",
            color: "#000",
            offset: 20,
            font: { weight: "bold", size: 14 },
            formatter: (value, context) =>
              context.dataIndex !== 0 && value !== 0 ? `INDEX:` : "",
          },
          index: {
            align: "end",
            anchor: "end",
            color: "#000",
            font: { weight: "bold", size: 16 },
            formatter: (value, context) => {
              return context.dataIndex !== 0 &&
                value !== 0 &&
                context.dataset?.index &&
                context.dataset?.index.length
                ? context.dataset?.index[context.dataIndex]
                : "";
            },
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          font: { size: 12, weight: "bold" },
          color: (context) => {
            const Index = context.index;
            const baseSizeValue = datasets[Index]?.participant[Index];
            return baseSizeValue < BASE_SIZE_MIN_VALUE
              ? "#6c757d" // previous color #dc3545(red)
              : "#6C757D";
          },
          callback: function (value, index, values) {
            const label = datasets[index]?.label;
            // const labelVal =
            //   label.length > 18 ? label.slice(0, 18) + "..." : label;
            // return `${labelVal} (n=${datasets[index]?.participant[index]})`; // Custom label based on the index
            return [
              ...wrapChartCharacters(label),
              `(n=${datasets[index]?.participant[index]})`,
            ];
          },
        },
      },
      y: {
        beginAtZero: true,
        suggestedMax: maxValue,
        title: {
          display: true,
          text: activeTag === "OZ" ? "Ounces" : "Grams",
          font: { size: 12, weight: "bold" },
        },
        ticks: {
          callback: (value) => {
            return value === 0
              ? "0"
              : Number.isInteger(value)
                ? `${value}.0`
                : `${value}`;
          },
          font: { size: 12, weight: "bold" },
        },
      },
    },
  };
};

export const timeOfDayChartOptions = (activeTag) => {
  return {
    responsive: true,
    aspectRatio: 3,
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "start",
        labels: {
          padding: 15,
          font: { size: 12, weight: "bold" },
          color: "#6C757D",
          generateLabels: function (chart) {
            // Customize label text
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            const datasetsVal = chart.data.datasets;
            labels.forEach((dataset, i) => {
              const baseSizeNVal = datasetsVal[i]?.participant?.reduce(
                (accumulator, currentValue) => accumulator + currentValue,
                0,
              );
              // Add N value text to each label
              dataset.text = `${dataset.text} (n=${baseSizeNVal})`;
              dataset.fontColor =
                baseSizeNVal < BASE_SIZE_MIN_VALUE
                  ? "#6c757d" // previous color #dc3545(red)
                  : "#6C757D";
            });

            return labels;
          },
        },
      },
      datalabels: {
        color: "#fff",
        anchor: "center",
        align: "center",
        font: { weight: "bold", size: 14 },
        formatter: (value) => (value !== 0 ? parseFloat(value).toFixed(1) : ""),
      },
    },
    scales: {
      x: {
        ticks: {
          font: { size: 12, weight: "bold" },
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: activeTag === "OZ" ? "Ounces" : "Grams",
          font: { size: 12, weight: "bold" },
        },
        ticks: {
          stepSize: 0.5,
          callback: (value) => (value !== 0 ? value : ""),
          font: { size: 12, weight: "bold" },
        },
      },
    },
  };
};

export const getDayOfWeekChartOptions = (dayOfWeekGraph, yLabel) => {
  const datasets = dayOfWeekGraph?.datasets || [];
  const maxValue =
    Math.max(...datasets.flatMap((dataset) => dataset.data), 0) + 1;

  return {
    aspectRatio: 3,
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "start",
        labels: {
          padding: 15,
          font: { size: 12, weight: "bold" },
          color: "#6C757D",
          generateLabels: function (chart) {
            // Customize label text
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            const datasetsVal = chart.data.datasets;
            labels.forEach((dataset, i) => {
              const baseSizeNVal = datasetsVal[i]?.participant?.reduce(
                (accumulator, currentValue) => accumulator + currentValue,
                0,
              );
              // Add N value text to each label
              dataset.text = `${dataset.text} (n=${baseSizeNVal})`;
              dataset.fontColor =
                baseSizeNVal < BASE_SIZE_MIN_VALUE
                  ? "#6c757d" // previous color #dc3545(red)
                  : "#6C757D";
            });

            return labels;
          },
        },
      },
      datalabels: {
        color: "#fff",
        anchor: "center",
        align: "center",
        font: { weight: "bold", size: 14 },
        formatter: (value) => (value !== 0 ? parseFloat(value).toFixed(1) : ""),
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: maxValue,
        title: {
          display: true,
          text: yLabel === "% OF TOTAL" ? "% of Total" : "# of Grams",
          font: { size: 12, weight: "bold" },
        },
        ticks: {
          stepSize: 25,
          callback: (value) =>
            yLabel === "% OF TOTAL" ? `${value}%` : `${value}`,
          font: { size: 12, weight: "bold" },
        },
      },
      x: {
        ticks: {
          font: { size: 12, weight: "bold" },
        },
      },
    },
  };
};

export const getWeekPartGraphOptions = (weekPartGraph, yLabel) => {
  const datasets = weekPartGraph?.datasets || [];
  const maxValue =
    Math.max(...datasets.flatMap((dataset) => dataset.data), 0) + 1;

  return {
    aspectRatio: 3,
    barThickness: datasets.length === 1 ? 200 : undefined,
    plugins: {
      legend: {
        display: true,
        position: "top",
        align: "start",
        labels: {
          padding: 15,
          font: { size: 12, weight: "bold" },
          color: "#6C757D",
          generateLabels: function (chart) {
            // Customize label text
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            const datasetsVal = chart.data.datasets;
            labels.forEach((dataset, i) => {
              const baseSizeNVal = datasetsVal[i]?.participant?.reduce(
                (accumulator, currentValue) => accumulator + currentValue,
                0,
              );
              // Add N value text to each label
              dataset.text = `${dataset.text} (n=${baseSizeNVal})`;
              dataset.fontColor =
                baseSizeNVal < BASE_SIZE_MIN_VALUE
                  ? "#6c757d" // previous color #dc3545(red)
                  : "#6C757D";
            });

            return labels;
          },
        },
      },
      datalabels: {
        color: "#fff",
        anchor: "center",
        align: "center",
        font: { weight: "bold", size: 14 },
        formatter: (value) => (value !== 0 ? parseFloat(value).toFixed(1) : ""),
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        suggestedMax: maxValue,
        title: {
          display: true,
          text: yLabel === "% OF TOTAL" ? "% of Total" : "# of Grams",
          font: { size: 12, weight: "bold" },
        },
        ticks: {
          callback: (value) =>
            yLabel === "% OF TOTAL" ? `${value}%` : `${value}`,
          font: { size: 12, weight: "bold" },
        },
      },
      x: {
        ticks: {
          font: { size: 12, weight: "bold" },
        },
      },
    },
  };
};
